import React, { useState, useEffect } from 'react';

import {
  IconMoodKid,
  IconStarFilled,
  IconCalendarEvent,
  IconCircleCheckFilled,
} from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import CustomImage from '@/components/CustomImage';
import CustomModal from '@/components/CustomModal';
import ProgressBar from '@/components/ProgressBar';
import LessonStudentDetailsSkeleton from '@/components/SkeletonLoader/LessonStudentDetailsSkeleton';
import { SummaryCard } from '@/components/SummaryCard';
import TagCapsule from '@/components/TagCapsule';
import { LessonStageConfig } from '@/constants/constant';
import { ELessonStageType } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import { getTeacherStudentLessonDetails } from '@/service/lessonService';
import { IStudentDetailModalData } from '@/types/student';
import {
  getAssetUrl,
  formatLastActive,
  formatUnixToLongDate,
  formatSecondsToDuration,
} from '@/utils';

interface IStudentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string | null;
  lessonId: string | null;
}

const StudentDetailsModal: React.FC<IStudentDetailsModalProps> = ({
  isOpen,
  onClose,
  studentId,
  lessonId,
}) => {
  const { formatText } = useFormatText();
  const [student, setStudent] = useState<IStudentDetailModalData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchStudentDetails = async () => {
      if (!studentId || !lessonId || !isOpen) return;

      setIsLoading(true);
      try {
        const response = await getTeacherStudentLessonDetails(
          lessonId,
          studentId
        );
        if (response?.status) {
          setStudent(response.data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStudentDetails();
  }, [studentId, lessonId, isOpen]);

  if (!isOpen) return null;

  const config = student?.learning_stage
    ? LessonStageConfig[student.learning_stage as ELessonStageType]
    : null;

  return (
    <CustomModal
      setIsOpen={onClose}
      heading={formatText('studentDetail.title')}
      isHeader
      position="right"
      className="max-w-150 w-full"
      childClassName="p-6 space-y-10"
    >
      {isLoading ? (
        <LessonStudentDetailsSkeleton />
      ) : (
        <>
          {/* Header Profile Section */}
          <div className="flex flex-col items-center gap-4 w-full">
            <UserAvatarList
              user={student?.name || ''}
              image={getAssetUrl(student?.profile_pic)}
              className="w-25 h-25 text-3xl!"
            />

            <h2 className="text-2xl font-bold text-neutral-primary text-center capitalize">
              {student?.name}
            </h2>

            <div className="flex items-center gap-2">
              {student?.class?.name && (
                <TagCapsule
                  value={student?.class?.name}
                  className="bg-blue-100 text-blue-800"
                />
              )}
              <TagCapsule
                value={formatText(config?.label ?? '-')}
                className={config?.className}
              />
            </div>

            <div className="flex items-center justify-center gap-8.75 w-full text-sm">
              <div className="flex items-center gap-1">
                <span className="text-neutral-secondary">
                  {formatText('studentDetail.age')}:
                </span>
                <span className="text-neutral-primary">
                  {student?.age
                    ? `${student?.age} ${formatText('studentDetail.years')}`
                    : '-'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-neutral-secondary">
                  {formatText('studentDetail.gender')}:
                </span>
                <span className="text-neutral-primary capitalize">
                  {student?.gender ?? '-'}
                </span>
              </div>
            </div>
          </div>

          {/* Student Details Grid */}
          <div className="grid grid-cols-2 gap-y-6 w-full">
            {/* Full Name */}
            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-neutral-hover">
                <IconMoodKid size={20} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm leading-5 text-neutral-secondary">
                  {formatText('studentDetail.fullName')}
                </span>
                <span className="text-base font-medium text-neutral-primary capitalize">
                  {student?.name}
                </span>
              </div>
            </div>

            {/* Date of Birth */}
            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-neutral-hover">
                <IconCalendarEvent size={20} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm leading-5 text-neutral-secondary">
                  {formatText('studentDetail.dateOfBirth')}
                </span>
                <span className="text-base font-medium text-neutral-primary">
                  {student?.birth_date
                    ? formatUnixToLongDate(Number(student?.birth_date))
                    : '-'}
                  {student?.age !== undefined && student?.age > 0 && (
                    <span className="text-neutral-secondary text-sm ml-1">
                      ({student?.age} {formatText('studentDetail.years')})
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Progress Summary */}
          <div className="space-y-4 w-full">
            <h3 className="text-lg font-semibold text-neutral-primary">
              {formatText('studentDetail.progressSummary')}
            </h3>
            <div className="rounded-2xl border border-neutral-light-200 p-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-neutral-secondary">
                  {formatText('studentDetail.overallProgress')}
                </span>
                <span className="font-semibold text-neutral-primary">
                  {student?.progress_summary?.progress ?? 0}%
                </span>
              </div>

              <ProgressBar
                value={student?.progress_summary?.progress ?? 0}
                showPercentage={false}
                height="h-2"
              />

              <div className="flex gap-4">
                <SummaryCard
                  label={formatText('studentDetail.starsEarned')}
                  value={student?.progress_summary?.stars_earned ?? 0}
                  icon={
                    <IconStarFilled
                      size={24}
                      className="mx-auto text-yellow-500"
                    />
                  }
                  bg="bg-warning-light-100"
                  text="text-warning-primary"
                  sub="text-warning-secondary"
                  variant="highlight"
                  className="flex-1"
                />
                <SummaryCard
                  label={formatText('studentDetail.games')}
                  value={student?.progress_summary?.games_completed ?? 0}
                  bg="bg-green-50"
                  text="text-success-primary"
                  sub="text-success-secondary"
                  variant="highlight"
                  className="flex-1"
                />
                <SummaryCard
                  label={formatText('studentDetail.avgScreenTime')}
                  value={formatSecondsToDuration(
                    student?.progress_summary?.average_screen_time ?? 0
                  )}
                  bg="bg-indigo-light-50"
                  text="text-indigo-900"
                  sub="text-indigo-secondary"
                  variant="highlight"
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="inline-flex flex-col gap-4 w-full">
            <div className="border-b border-neutral-200 pb-1">
              <h3 className="text-lg font-semibold text-neutral-primary leading-7">
                {formatText('studentDetail.activityTimeline')}
              </h3>
            </div>

            <div className="relative flex flex-col gap-6">
              {(student?.activity_timeline ?? []).length > 0 ? (
                (student?.activity_timeline ?? []).map((a) => (
                  <React.Fragment
                    key={`${a?.timestamp}-${a?.title}-${a?.type}`}
                  >
                    {/* Vertical line */}
                    <div className="absolute left-5 rtl:right-5 rtl:left-auto top-0 h-full w-px bg-neutral-200" />
                    <div className="flex items-center gap-4">
                      {/* Timeline Dot */}
                      <div className="relative z-10 flex p-2 items-center justify-center rounded-full border border-neutral-300 bg-white">
                        <IconCircleCheckFilled
                          size={24}
                          className="text-brand-default"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex flex-1 items-center justify-between gap-2">
                        <div className="space-x-2 min-w-0">
                          <span className="text-base font-semibold text-neutral-primary max-w-80 wrap-anywhere">
                            {a?.title}
                          </span>
                          {a?.quarters !== null &&
                            a?.quarters !== undefined && (
                              <TagCapsule
                                value={`Q${a.quarters}`}
                                className="bg-purple-50 text-brand-default"
                              />
                            )}
                          {a?.rounds !== null && a?.rounds !== undefined && (
                            <TagCapsule
                              value={`R${a.rounds}`}
                              className="bg-neutral-100 text-neutral-primary"
                            />
                          )}
                        </div>
                        <span className="text-sm text-neutral-secondary shrink-0">
                          {formatLastActive(a?.timestamp, formatText)}
                        </span>
                      </div>
                    </div>
                  </React.Fragment>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <CustomImage
                    src={'/assets/images/lesson-breakdown-empty.png'}
                    alt="noData"
                    width={100}
                    height={100}
                  />
                  <p className="text-neutral-900 font-semibold">
                    {formatText('common.no-data-found')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </CustomModal>
  );
};

export default StudentDetailsModal;
